import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';
import { isUserAdmin } from '../helpers/adminAuth';
import reportsService, { ReportRow, ReportSnapshotSummary, SnapshotsResponse } from '../services/reportsService';
import SnapshotCard from '../components/AdminReports/SnapshotCard';
import LifecycleBadge from '../components/LifecycleBadge/LifecycleBadge';
import { exportSnapshotToCsv } from '../utils/snapshotCsvExport';
import './AdminReportsPage.css';

const SNAPSHOTS_PAGE_SIZE = 12;

/**
 * Admin page for viewing and managing API reports analysis
 */
export const AdminReportsPage: React.FC = () => {
  const { authState } = useOktaAuth();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // View state
  const [viewMode, setViewMode] = useState<'governance' | 'apim'>('governance');

  // Filter state
  const [search, setSearch] = useState('');
  const [lineOfBusiness, setLineOfBusiness] = useState('');
  const [product, setProduct] = useState('');
  const [area, setArea] = useState('');
  const [pointOfContact, setPointOfContact] = useState('');
  const [status, setStatus] = useState<'compliant' | 'partially-compliant' | 'non-compliant' | 'not-analyzed' | ''>('');
  const [enrolled, setEnrolled] = useState<'true' | 'false' | ''>(''); // New Enrolled state
  const [apimPublishedStatus, setApimPublishedStatus] = useState<string>(''); // New APIM filter
  const [lifecycleStage, setLifecycleStage] = useState<string>(''); // New Lifecycle Stage filter
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [pipelineEnrolled, setPipelineEnrolled] = useState(0);

  // Filter options (fetched comprehensively on mount)
  const [lobOptions, setLobOptions] = useState<string[]>([]);
  const [productOptions, setProductOptions] = useState<string[]>([]);
  const [areaOptions, setAreaOptions] = useState<string[]>([]);
  const [pocOptions, setPocOptions] = useState<string[]>([]);
  const [lifecycleOptions, setLifecycleOptions] = useState<string[]>([]);

  // Expandable rows - Two levels: parents and findings
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(new Set());

  // Filter modal state
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    search,
    lineOfBusiness,
    product,
    area,
    pointOfContact,
    status,
    enrolled,
    apimPublishedStatus,
    lifecycleStage
  });

  // Run analysis dialog
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [forceAnalysis, setForceAnalysis] = useState(false);
  const [runLoading, setRunLoading] = useState(false);

  // Snapshots tab state
  const [activeTab, setActiveTab] = useState<'live' | 'snapshots' | 'apim'>('live');
  const [snapshots, setSnapshots] = useState<ReportSnapshotSummary[]>([]);
  const [snapshotsPage, setSnapshotsPage] = useState(1);
  const [snapshotsTotal, setSnapshotsTotal] = useState(0);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [snapshotsError, setSnapshotsError] = useState<string | null>(null);
  const [triggeringSnapshot, setTriggeringSnapshot] = useState(false);

  // Fetch filter options once on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      //Gate the fetch behind the admin check to prevent unauthorized data loading
      if (!authState?.isAuthenticated || !isUserAdmin(authState)) return;
      try {
        // Fetch with reasonable pageSize (500 instead of 10000) to avoid backend performance issues
        const response = await reportsService.getReport(
          authState, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, 1, 500, 'json', undefined, undefined
        );

        if (response && response.data) {
          // Filter out 'N/A' values and empty strings when extracting unique options
          const uniqueLobs = Array.from(new Set(response.data.map((r: ReportRow) => r.lineOfBusiness).filter(v => v && v !== 'N/A')));
          const uniqueProducts = Array.from(new Set(response.data.map((r: ReportRow) => r.product).filter(v => v && v !== 'N/A')));
          const uniqueAreas = Array.from(new Set(response.data.map((r: ReportRow) => r.area).filter(v => v && v !== 'N/A')));
          const uniquePocs = Array.from(new Set(response.data.map((r: ReportRow) => r.pointOfContact).filter(v => v && v !== 'N/A')));
          const childLifecycles = response.data.flatMap((r: ReportRow) =>
            r.childVersions?.map((v: any) => v.lifecycleStage?.toLowerCase()).filter(Boolean) || []
          );
          const uniqueLifecycles = Array.from(new Set([
            ...response.data.map((r: ReportRow) => r.lifecycleStage?.toLowerCase()),
            ...childLifecycles,
          ].filter(v => v && v !== 'n/a'))).sort();

          setLobOptions(uniqueLobs as string[]);
          setProductOptions(uniqueProducts as string[]);
          setAreaOptions(uniqueAreas as string[]);
          setPocOptions(uniquePocs as string[]);
          setLifecycleOptions(uniqueLifecycles as string[]);
        }
      } catch (err) {
        console.error('Failed to load comprehensive filter options:', err);
      }
    };

    fetchFilterOptions();
  }, [authState]);

  // Fetch report data for the table
  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    setReportData([]); // Clear previous data while loading

    try {
      const isApim = viewMode === 'apim';
      
      // Convert enrolled string state to boolean for the service
      let enrolledValue: boolean | undefined = undefined;
      if (!isApim && enrolled !== '') {
        enrolledValue = enrolled === 'true';
      }
      
      const response = await reportsService.getReport(
        authState,
        search || undefined,
        undefined, // ownerTeam - not using for now
        lineOfBusiness || undefined,
        product || undefined,
        area || undefined,
        pointOfContact || undefined,
        isApim ? undefined : (status || undefined),
        enrolledValue, // Only pass if explicitly set in governance mode
        page,
        pageSize,
        'json',
        isApim ? true : undefined, // developerProgram filter for APIM view only
        isApim ? (apimPublishedStatus || undefined) : undefined, // apimPublishedStatus filter for APIM view only
        !isApim ? (lifecycleStage || undefined) : undefined // lifecycleStage filter for governance view only
      );

      if (response && response.data !== undefined) {
        setReportData(response.data);
        setTotal(response.total || response.data.length);
        if (response.summary) {
          setPipelineEnrolled(response.summary.pipelineEnrolledCount || 0);
        }
      } else {
        setError('No response data received from server');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load reports';
      setError(message);
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch report when filters change
  useEffect(() => {
    // Gate the fetch behind the admin check
    if (authState?.isAuthenticated && isUserAdmin(authState)) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, lineOfBusiness, product, area, pointOfContact, status, enrolled, apimPublishedStatus, lifecycleStage, viewMode, page, authState]);

  const handleFilterChange = () => {
    setPage(1);
    fetchReport();
  };

  const handleOpenFilterModal = () => {
    setTempFilters({
      search,
      lineOfBusiness,
      product,
      area,
      pointOfContact,
      status,
      enrolled,
      apimPublishedStatus,
      lifecycleStage
    });
    setFilterModalOpen(true);
  };

  const handleApplyFilters = () => {
    setSearch(tempFilters.search);
    setLineOfBusiness(tempFilters.lineOfBusiness);
    setProduct(tempFilters.product);
    setArea(tempFilters.area);
    setPointOfContact(tempFilters.pointOfContact);
    setStatus(tempFilters.status);
    setEnrolled(tempFilters.enrolled);
    setApimPublishedStatus(tempFilters.apimPublishedStatus);
    setLifecycleStage(tempFilters.lifecycleStage);
    setPage(1);
    setFilterModalOpen(false);
  };

  const handleResetFiltersModal = () => {
    setTempFilters({
      search: '',
      lineOfBusiness: '',
      product: '',
      area: '',
      pointOfContact: '',
      status: '',
      enrolled: '',
      apimPublishedStatus: '',
      lifecycleStage: ''
    });
  };

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
  };

  const removeFilter = (filterType: string) => {
    setPage(1);
    if (filterType === 'lineOfBusiness') { setLineOfBusiness(''); setTempFilters(prev => ({...prev, lineOfBusiness: ''})); }
    if (filterType === 'product') { setProduct(''); setTempFilters(prev => ({...prev, product: ''})); }
    if (filterType === 'area') { setArea(''); setTempFilters(prev => ({...prev, area: ''})); }
    if (filterType === 'pointOfContact') { setPointOfContact(''); setTempFilters(prev => ({...prev, pointOfContact: ''})); }
    if (filterType === 'status') { setStatus(''); setTempFilters(prev => ({...prev, status: ''})); }
    if (filterType === 'enrolled') { setEnrolled(''); setTempFilters(prev => ({...prev, enrolled: ''})); }
    if (filterType === 'apimPublishedStatus') { setApimPublishedStatus(''); setTempFilters(prev => ({...prev, apimPublishedStatus: ''})); }
    if (filterType === 'lifecycleStage') { setLifecycleStage(''); setTempFilters(prev => ({...prev, lifecycleStage: ''})); }
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (search) count++;
    if (lineOfBusiness) count++;
    if (product) count++;
    if (area) count++;
    if (pointOfContact) count++;
    if (viewMode === 'governance' && status) count++;
    if (viewMode === 'governance' && enrolled) count++;
    if (viewMode === 'governance' && lifecycleStage) count++;
    if (viewMode === 'apim' && apimPublishedStatus) count++;
    return count;
  };

  const formatStatus = (s: string) => {
    if (s === 'compliant') return 'Compliant';
    if (s === 'partially-compliant') return 'Partially Compliant';
    if (s === 'non-compliant') return 'Non-Compliant';
    if (s === 'not-analyzed') return 'Not Analyzed';
    return s;
  };

  const formatApimStatus = (s: string) => {
    if (s === 'yes') return 'Yes';
    if (s === 'no') return 'No';
    return 'N/A';
  };

  const handleRunAnalysis = async () => {
    try {
      setRunLoading(true);
      setError(null);

      await reportsService.runAnalysis(authState, forceAnalysis);

      // Refresh data
      await fetchReport();
      setRunDialogOpen(false);
      setForceAnalysis(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to run analysis';
      setError(message);
    } finally {
      setRunLoading(false);
    }
  };

  // Snapshot handlers
  const fetchSnapshots = async (pageNumber: number = snapshotsPage) => {
    setSnapshotsLoading(true);
    setSnapshotsError(null);

    try {
      const offset = (pageNumber - 1) * SNAPSHOTS_PAGE_SIZE;
      const result: SnapshotsResponse = await reportsService.getSnapshots(authState, SNAPSHOTS_PAGE_SIZE, offset);

      // Page no longer exists (e.g. snapshots were removed); jump to the last valid page
      const lastPage = Math.max(1, Math.ceil(result.total / SNAPSHOTS_PAGE_SIZE));
      if (pageNumber > lastPage) {
        setSnapshotsPage(lastPage);
        return;
      }

      setSnapshots(result.snapshots);
      setSnapshotsTotal(result.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load snapshots';
      setSnapshotsError(message);
      console.error('Error fetching snapshots:', err);
    } finally {
      setSnapshotsLoading(false);
    }
  };

  const handleGenerateSnapshot = async () => {
    try {
      setTriggeringSnapshot(true);
      setError(null);

      await reportsService.triggerSnapshot(authState);

      // Newest snapshot is on page 1; the page effect refetches if we are elsewhere
      if (snapshotsPage === 1) {
        await fetchSnapshots(1);
      } else {
        setSnapshotsPage(1);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate snapshot';
      setError(message);
      console.error('Error triggering snapshot:', err);
    } finally {
      setTriggeringSnapshot(false);
    }
  };

  const handleSnapshotCardClick = (snapshot: ReportSnapshotSummary) => {
    navigate(`/admin/reports/snapshots/${snapshot.id}`);
  };

  const handleCloseSnapshotModal = () => {
    // No longer needed - navigation handles viewing snapshots
  };

  // Fetch snapshots when the tab or snapshot page changes
  useEffect(() => {
    if (activeTab === 'snapshots') {
      fetchSnapshots(snapshotsPage);
    }
  }, [activeTab, snapshotsPage]);

  // Verify admin access
  if (authState?.isPending) {
    return <div className="container mt-4"><div className="loading">Verifying permissions...</div></div>;
  }

  if (!isUserAdmin(authState)) {
    return (
      <div className="container mt-4">
        <div className="alert alert-error">
          <h4>Access Denied</h4>
          <p>You do not have permission to access the Reports page. Only admins can view this section.</p>
        </div>
      </div>
    );
  }

  const handleExportCsv = async () => {
    try {
      setLoading(true);
      const isApim = viewMode === 'apim';
      const response = await reportsService.getReport(
        authState,
        search || undefined,
        undefined, 
        lineOfBusiness || undefined,
        product || undefined,
        area || undefined,
        pointOfContact || undefined,
        isApim ? undefined : (status || undefined),
        isApim ? undefined : (enrolled === '' ? undefined : enrolled === 'true'),
        1,
        10000, 
        'json', 
        isApim ? true : undefined,
        isApim ? (apimPublishedStatus || undefined) : undefined 
      );

      if (response && response.data) {
        reportsService.exportToCsv(response.data, `report-${viewMode}.csv`, viewMode);
      }
    } catch (err) {
      console.error('Failed to export CSV', err);
      setError('Failed to export CSV');
    } finally {
      setLoading(false);
    }
  };

  const toggleParentExpansion = (apiId: string) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(apiId)) {
        next.delete(apiId);
        // Also collapse all children's findings when parent collapses
        setExpandedFindings(new Set());
      } else {
        next.add(apiId);
      }
      return next;
    });
  };

  const toggleFindingsExpansion = (versionId: string) => {
    setExpandedFindings(prev => {
      const next = new Set(prev);
      if (next.has(versionId)) {
        next.delete(versionId);
      } else {
        next.add(versionId);
      }
      return next;
    });
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid';
      return date.toLocaleDateString();
    } catch {
      return 'Invalid';
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  
  if (authState?.isPending) {
    return <div className="container mt-4"><div className="loading">Verifying permissions...</div></div>;
  }

  if (!isUserAdmin(authState)) {
      return (
        <div className="container mt-4">
          <div className="alert alert-error">
            <h4>Access Denied</h4>
            <p>You do not have permission to access the Reports page. Only admins can view this section.</p>
          </div>
        </div>
      );
  }
  const snapshotsTotalPages = Math.ceil(snapshotsTotal / SNAPSHOTS_PAGE_SIZE);
  
  return (
    <div className="reports-page">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="reports-container">

        {/* Header */}
        <div className="reports-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>{activeTab === 'snapshots' ? 'Snapshot Reports' : viewMode === 'governance' ? 'Admin Reports' : 'APIM Coverage Report'}</h1>
          {activeTab !== 'snapshots' && (
            <div className="summary-stats" style={{ display: 'flex', gap: '15px' }}>
              <span className="badge badge-info" style={{ fontSize: '14px', padding: '8px 12px' }}>
                Total APIs: {total}
              </span>
              {viewMode === 'governance' && (
                <span className="badge badge-compliant" style={{ fontSize: '14px', padding: '8px 12px' }}>
                  Pipeline Enrolled: {pipelineEnrolled}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="tabs-container">
          <button
            className={`tab-button ${activeTab === 'live' ? 'active' : ''}`}
            onClick={() => { setActiveTab('live'); setViewMode('governance'); setPage(1); }}
          >
            Live Report
          </button>
          <button
            className={`tab-button ${activeTab === 'snapshots' ? 'active' : ''}`}
            onClick={() => setActiveTab('snapshots')}
          >
            Snapshots
          </button>
          <button
            className={`tab-button ${activeTab === 'apim' ? 'active' : ''}`}
            onClick={() => { setActiveTab('apim'); setViewMode('apim'); setPage(1); }}
          >
            APIM Coverage
          </button>
          <button
            className="tab-button"
            onClick={() => navigate('/admin/ci-pass-rates')}
          >
            CI Pass Rate Reports
          </button>
        </div>

        {/* Live Report and APIM Coverage Tabs (shared table) */}
        {(activeTab === 'live' || activeTab === 'apim') && (
          <>
        {/* Info label for APIM view */}
        {viewMode === 'apim' && (
          <div className="info-box">
            Showing only APIs enrolled in the developer program. Click the expand button (▶) to view individual versions and their publication status per environment.
          </div>
        )}
                {/* Filter Bar */}
        <div className="filter-bar">
          <input
            type="text"
            className="search-input"
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleFilterChange()}
            placeholder="Search API Name..."
          />
          <button
            className="filter-button"
            onClick={handleOpenFilterModal}
            title="Open filters"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.5 5.83333H17.5M5.83333 10H14.1667M8.33333 14.1667H11.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {getActiveFilterCount() > 0 && (
              <span className="filter-badge">{getActiveFilterCount()}</span>
            )}
          </button>
          
          {/* Active Filter Tags - only rendered when a filter is active */}
          {getActiveFilterCount() > 0 && (
            <div className="active-filters">
              {lineOfBusiness && <span className="filter-tag" onClick={() => removeFilter('lineOfBusiness')} style={{ cursor: 'pointer' }} title="Remove filter">LOB: {lineOfBusiness} ✕</span>}
              {product && <span className="filter-tag" onClick={() => removeFilter('product')} style={{ cursor: 'pointer' }} title="Remove filter">Product: {product} ✕</span>}
              {area && <span className="filter-tag" onClick={() => removeFilter('area')} style={{ cursor: 'pointer' }} title="Remove filter">Area: {area} ✕</span>}
              {pointOfContact && <span className="filter-tag" onClick={() => removeFilter('pointOfContact')} style={{ cursor: 'pointer' }} title="Remove filter">POC: {pointOfContact} ✕</span>}

              {viewMode === 'governance' && status && <span className="filter-tag" onClick={() => removeFilter('status')} style={{ cursor: 'pointer' }} title="Remove filter">Status: {formatStatus(status)} ✕</span>}
              {viewMode === 'governance' && enrolled && <span className="filter-tag" onClick={() => removeFilter('enrolled')} style={{ cursor: 'pointer' }} title="Remove filter">Enrolled: {enrolled === 'true' ? 'Yes' : 'No'} ✕</span>}
              {viewMode === 'governance' && lifecycleStage && <span className="filter-tag" onClick={() => removeFilter('lifecycleStage')} style={{ cursor: 'pointer' }} title="Remove filter">Lifecycle: {lifecycleStage} ✕</span>}

              {viewMode === 'apim' && apimPublishedStatus && <span className="filter-tag" onClick={() => removeFilter('apimPublishedStatus')} style={{ cursor: 'pointer' }} title="Remove filter">Published: {formatApimStatus(apimPublishedStatus)} ✕</span>}
            </div>
          )}
        </div>

        {/* Filter Modal */}
        {filterModalOpen && (
          <div className="filter-modal-overlay" onClick={handleCloseFilterModal}>
            <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
              <div className="filter-modal-header">
                <h3>Filter Reports</h3>
                <button
                  className="close-button"
                  onClick={handleCloseFilterModal}
                  type="button"
                >
                  ✕
                </button>
              </div>
              <div className="filter-modal-body">
                <div className="filter-section">
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Line of Business</label>
                      <select
                        value={tempFilters.lineOfBusiness}
                        onChange={(e) => setTempFilters({...tempFilters, lineOfBusiness: e.target.value})}
                      >
                        <option value="">All</option>
                        {lobOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>Product</label>
                      <select
                        value={tempFilters.product}
                        onChange={(e) => setTempFilters({...tempFilters, product: e.target.value})}
                      >
                        <option value="">All</option>
                        {productOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>Area</label>
                      <select
                        value={tempFilters.area}
                        onChange={(e) => setTempFilters({...tempFilters, area: e.target.value})}
                      >
                        <option value="">All</option>
                        {areaOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Point of Contact</label>
                      <select
                        value={tempFilters.pointOfContact}
                        onChange={(e) => setTempFilters({...tempFilters, pointOfContact: e.target.value})}
                      >
                        <option value="">All</option>
                        {pocOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    {viewMode === 'governance' && (
                      <>
                        <div className="filter-group">
                          <label>Status</label>
                          <select
                            value={tempFilters.status}
                            onChange={(e) => setTempFilters({...tempFilters, status: e.target.value as any})}
                          >
                            <option value="">All</option>
                            <option value="compliant">Compliant</option>
                            <option value="partially-compliant">Partially Compliant</option>
                            <option value="non-compliant">Non-Compliant</option>
                            <option value="not-analyzed">Not Analyzed</option>
                          </select>
                        </div>
                        <div className="filter-group">
                          <label>Enrolled</label>
                          <select
                            value={tempFilters.enrolled}
                            onChange={(e) => setTempFilters({ ...tempFilters, enrolled: e.target.value as 'true' | 'false' | '' })}
                          >
                            <option value="">All</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        </div>
                        <div className="filter-group">
                          <label>Lifecycle Stage</label>
                          <select
                            value={tempFilters.lifecycleStage}
                            onChange={(e) => setTempFilters({ ...tempFilters, lifecycleStage: e.target.value })}
                          >
                            <option value="">All</option>
                            {lifecycleOptions.map(opt => (
                              <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1).toLowerCase()}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}

                    {viewMode === 'apim' && (
                       <div className="filter-group">
                          <label>APIM Published</label>
                          <select
                            value={tempFilters.apimPublishedStatus}
                            onChange={(e) => setTempFilters({ ...tempFilters, apimPublishedStatus: e.target.value })}
                          >
                            <option value="">All</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                       </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="filter-modal-footer">
                <button className="reset-button" onClick={handleResetFiltersModal}>
                  Reset
                </button>
                <button className="apply-button" onClick={handleApplyFilters}>
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="table-wrapper">
          {loading ? (
            <div className="loading">Loading...</div>
          ) : reportData.length === 0 ? (
            <div className="no-data">No APIs found</div>
          ) : (
            <>
              <table className="reports-table">
                <thead>
                  {viewMode === 'governance' ? (
                    <tr>
                      <th>API Name</th>
                      <th>Line of Business</th>
                      <th>Product</th>
                      <th>Area</th>
                      <th>Versions</th>
                      <th>Point of Contact</th>
                      <th>Enrolled</th>
                      <th>Lifecycle Stage</th>
                      <th>Status</th>
                      <th>Quality Score</th>
                      <th>Compliance Score</th>
                      <th>Findings</th>
                      <th>Last Analyzed</th>
                      <th>Action</th>
                    </tr>
                  ) : (
                    <tr>
                      <th>API Name</th>
                      <th>Point of Contact</th>
                      <th>Area</th>
                      <th>Versions & Environments</th>
                      <th>APIM Products</th>
                      <th>Published Status</th>
                      <th>Last Discovered</th>
                      <th>Action</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {reportData.map(row => (
                    <React.Fragment key={row.apiId}>
                      <tr>
                        {viewMode === 'governance' ? (
                          <>
                            <td>
                              {row.childVersions && row.childVersions.length > 0 ? (
                                <div className="findings-cell">
                                  <button
                                    className="btn-expand-small"
                                    onClick={() => toggleParentExpansion(row.apiId)}
                                    title="Toggle versions"
                                  >
                                    {expandedParents.has(row.apiId) ? '▼' : '▶'}
                                  </button>
                                  <span>{row.fullApiName}</span>
                                </div>
                              ) : (
                                row.fullApiName
                              )}
                            </td>
                            <td>{row.lineOfBusiness || 'N/A'}</td>
                            <td>{row.product || 'N/A'}</td>
                            <td>{row.area || 'N/A'}</td>
                            <td>{row.apiVersion || 'N/A'}</td>
                            <td>{row.pointOfContact || 'N/A'}</td>
                            <td>
                              <span className={row.isEnrolled ? "badge badge-compliant" : "badge badge-not-analyzed"}>
                                {row.isEnrolled ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td>
                              {row.lifecycleStage ? (
                                <LifecycleBadge stage={row.lifecycleStage} />
                              ) : (
                                <span style={{ color: '#999' }}>N/A</span>
                              )}
                            </td>
                            <td>
                              {row.childVersions && row.childVersions.length > 0 ? (
                                <span style={{ color: '#666' }}>—</span>
                              ) : (
                                <span className={`badge badge-${row.status || 'unknown'}`}>
                                  {row.status === 'compliant' ? 'Compliant' :
                                   row.status === 'partially-compliant' ? 'Partially Compliant' :
                                   row.status === 'non-compliant' ? 'Non-Compliant' :
                                   'Not Analyzed'}
                                </span>
                              )}
                            </td>
                            <td>{row.childVersions && row.childVersions.length > 0 ? '—' : (row.qualityScore !== undefined ? `${row.qualityScore}%` : 'N/A')}</td>
                            <td>{row.childVersions && row.childVersions.length > 0 ? '—' : (row.complianceScore !== undefined ? `${row.complianceScore}%` : 'N/A')}</td>
                            <td>
                              {row.childVersions && row.childVersions.length > 0 ? (
                                '—'
                              ) : (row.findings && row.findings.length > 0 ? (
                                <div className="findings-cell">
                                  <button
                                    className="btn-expand-small"
                                    onClick={() => toggleFindingsExpansion(row.apiId)}
                                    title="Toggle findings"
                                  >
                                    {expandedFindings.has(row.apiId) ? '▼' : '▶'}
                                  </button>
                                  <span className="badge badge-info">{row.findings.length}</span>
                                </div>
                              ) : (
                                '0'
                              ))}
                            </td>
                            <td>{row.childVersions && row.childVersions.length > 0 ? '—' : formatDate(row.lastAnalyzedAt)}</td>
                          </>
                        ) : (
                          <>
                            <td>
                              {row.childVersions && row.childVersions.length > 0 ? (
                                <div className="findings-cell">
                                  <button
                                    className="btn-expand-small"
                                    onClick={() => toggleParentExpansion(row.apiId)}
                                    title="Toggle versions"
                                  >
                                    {expandedParents.has(row.apiId) ? '▼' : '▶'}
                                  </button>
                                  <span>{row.fullApiName}</span>
                                </div>
                              ) : (
                                row.fullApiName
                              )}
                            </td>
                            <td>{row.pointOfContact || 'N/A'}</td>
                            <td>{row.area || 'N/A'}</td>
                            <td>{row.childVersions ? `${row.childVersions.length} version(s)` : `${row.apiVersion || 'N/A'}${row.apimEnvironments ? ` (${row.apimEnvironments})` : ''}`}</td>
                            <td>{row.apimProducts || 'N/A'}</td>
                            <td>
                              <span className={`badge badge-${
                                row.apimPublishedStatus === 'yes' ? 'compliant' : 
                                row.apimPublishedStatus === 'no' ? 'non-compliant' : 'not-analyzed'
                              }`}>
                                {formatApimStatus(row.apimPublishedStatus || 'n/a')}
                              </span>
                            </td>
                            <td>{formatDate(row.apimLastDiscoveredAt)}</td>
                          </>
                        )}
                        
                        <td>
                          <button
                            className="btn btn-small btn-link"
                            onClick={() => navigate(`/spec-analysis/${row.apiId}`)}
                            title="View spec analysis"
                          >
                            See details →
                          </button>
                        </td>
                      </tr>

                      {/* Expanded child versions row (Governance view) */}
                      {viewMode === 'governance' && expandedParents.has(row.apiId) && row.childVersions && row.childVersions.length > 0 && (
                        <tr className="versions-row">
                          <td colSpan={14}>
                            <div className="versions-detail">
                              <table className="versions-table">
                                <thead>
                                  <tr>
                                    <th>Version</th>
                                    <th>Status</th>
                                    <th>Quality Score</th>
                                    <th>Compliance Score</th>
                                    <th>Findings</th>
                                    <th>Last Analyzed</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {row.childVersions.map((version) => (
                                    <React.Fragment key={version.versionId}>
                                      <tr className="version-detail-row">
                                        <td>{version.versionNumber}</td>
                                        <td>
                                          <span className={`badge badge-${version.status || 'unknown'}`}>
                                            {version.status === 'compliant' ? 'Compliant' :
                                             version.status === 'partially-compliant' ? 'Partially Compliant' :
                                             version.status === 'non-compliant' ? 'Non-Compliant' :
                                             'Not Analyzed'}
                                          </span>
                                        </td>
                                        <td>{version.qualityScore !== undefined ? `${version.qualityScore}%` : 'N/A'}</td>
                                        <td>{version.complianceScore !== undefined ? `${version.complianceScore}%` : 'N/A'}</td>
                                        <td>
                                          {version.findings && version.findings.length > 0 ? (
                                            <div className="findings-cell">
                                              <button
                                                className="btn-expand-small"
                                                onClick={() => toggleFindingsExpansion(version.versionId)}
                                                title="Toggle findings"
                                              >
                                                {expandedFindings.has(version.versionId) ? '▼' : '▶'}
                                              </button>
                                              <span className="badge badge-info">{version.findings.length}</span>
                                            </div>
                                          ) : (
                                            '0'
                                          )}
                                        </td>
                                        <td>{formatDate(version.lastAnalyzedAt)}</td>
                                        <td>
                                          <button
                                            className="btn btn-small btn-link"
                                            onClick={() => navigate(`/spec-analysis/${version.versionId}`)}
                                            title="View version details"
                                          >
                                            View →
                                          </button>
                                        </td>
                                      </tr>

                                      {/* Findings Expansion Row (Third Level) */}
                                      {expandedFindings.has(version.versionId) && version.findings && version.findings.length > 0 && (
                                        <tr className="findings-row">
                                          <td colSpan={7}>
                                            <div className="findings-detail">
                                              <ul className="findings-list">
                                                {version.findings.map((finding, fidx) => (
                                                  <li key={fidx} className={`finding-${finding.severity}`}>
                                                    <span className="finding-icon">
                                                      {finding.severity === 'error' ? '✕' : finding.severity === 'warn' ? '⚠' : 'ℹ'}
                                                    </span>
                                                    <span className="finding-code">{finding.code}</span>
                                                    <span className="finding-message">{finding.message}</span>
                                                    {finding.line && <span className="finding-line">:{finding.line}</span>}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Expanded findings row for standalone APIs (Governance view) */}
                      {viewMode === 'governance' && expandedFindings.has(row.apiId) && !row.childVersions && row.findings && row.findings.length > 0 && (
                        <tr className="findings-row">
                          <td colSpan={14}>
                            <div className="findings-detail">
                              <ul className="findings-list">
                                {row.findings.map((finding, idx) => (
                                  <li key={idx} className={`finding-${finding.severity}`}>
                                    <span className="finding-icon">
                                      {finding.severity === 'error' ? '✕' : finding.severity === 'warn' ? '⚠' : 'ℹ'}
                                    </span>
                                    <span className="finding-code">{finding.code}</span>
                                    <span className="finding-message">{finding.message}</span>
                                    {finding.line && <span className="finding-line">:{finding.line}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Expanded versions row (only for APIM view) */}
                      {viewMode === 'apim' && expandedParents.has(row.apiId) && row.childVersions && row.childVersions.length > 0 && (
                        <tr className="versions-row">
                          <td colSpan={8}>
                            <div className="versions-detail">
                              <table className="versions-table">
                                <thead>
                                  <tr>
                                    <th>Version</th>
                                    <th>Environments</th>
                                    <th>Published Status</th>
                                    <th>Last Discovered</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {row.childVersions.map((version, idx) => (
                                    <tr key={idx} className="version-detail-row">
                                      <td>{version.versionNumber}</td>
                                      <td>
                                        {version.apimEnvironments ? (
                                          <span className="badge badge-info">{version.apimEnvironments}</span>
                                        ) : (
                                          'Not published'
                                        )}
                                      </td>
                                      <td>
                                        <span className={`badge badge-${
                                          version.apimPublishedStatus === 'yes' ? 'compliant' : 
                                          'non-compliant'
                                        }`}>
                                          {formatApimStatus(version.apimPublishedStatus || 'n/a')}
                                        </span>
                                      </td>
                                      <td>{formatDate(version.apimLastDiscoveredAt)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn btn-small"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                  >
                    ← Previous
                  </button>
                  <span className="pagination-info">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-small"
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="btn btn-primary" onClick={() => setRunDialogOpen(true)}>
            Run Analysis
          </button>
          <button className="btn btn-secondary" onClick={handleExportCsv} disabled={loading}>
            Export to CSV
          </button>
        </div>

        {/* Run Analysis Dialog */}
        {runDialogOpen && (
          <div className="modal-overlay" onClick={() => !runLoading && setRunDialogOpen(false)} style={{ pointerEvents: 'auto' }}>
            <div className="modal-dialog" onClick={(e: React.MouseEvent) => e.stopPropagation()} style={{ pointerEvents: 'auto' }}>
              <div className="modal-header">
                <h2>Run Analysis</h2>
                <button
                  className="modal-close"
                  onClick={() => !runLoading && setRunDialogOpen(false)}
                  disabled={runLoading}
                  type="button"
                  style={{ pointerEvents: 'auto' }}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>This will analyze all pipeline-registered APIs against the governance ruleset.</p>
                <div className="form-group">
                  <label>Analysis Mode:</label>
                  <select
                    value={forceAnalysis ? 'force' : 'incremental'}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setForceAnalysis(e.target.value === 'force')}
                    style={{ pointerEvents: 'auto' }}
                  >
                    <option value="incremental">Incremental (skip recent analyses)</option>
                    <option value="force">Force (re-analyze all APIs)</option>
                  </select>
                </div>
                <p className="mode-description">
                  {forceAnalysis
                    ? '✓ Will re-analyze all APIs regardless of last analysis time'
                    : '✓ Will skip APIs analyzed recently or without changes'}
                </p>
              </div>
              <div className="modal-footer" style={{ pointerEvents: 'auto' }}>
                <button className="btn btn-secondary" onClick={() => setRunDialogOpen(false)} disabled={runLoading} style={{ pointerEvents: 'auto' }}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleRunAnalysis} disabled={runLoading} style={{ pointerEvents: 'auto' }}>
                  {runLoading ? 'Running...' : 'Run Analysis'}
                </button>
              </div>
            </div>
          </div>
        )}
          </>
        )}

        {/* Snapshots Tab */}
        {activeTab === 'snapshots' && (
          <div className="snapshots-tab">
            <div className="snapshots-header">
              <div>
                <h2>Historical Compliance Snapshots</h2>
                <p className="snapshots-subtitle">
                  Weekly snapshots — each one covers a 7-day period.
                </p>
              </div>
              <button
                className="btn btn-primary"
                onClick={handleGenerateSnapshot}
                disabled={triggeringSnapshot}
              >
                {triggeringSnapshot ? 'Generating...' : 'Generate New Snapshot'}
              </button>
            </div>

            {snapshotsError && <div className="alert alert-error">{snapshotsError}</div>}

            {snapshotsLoading ? (
              <div className="loading">Loading snapshots...</div>
            ) : snapshots.length === 0 ? (
              <div className="empty-state">
                <p>No snapshots available yet. Generate your first snapshot to get started.</p>
              </div>
            ) : (
              <>
                <div className="snapshot-list">
                  {snapshots.map((snapshot) => (
                    <SnapshotCard
                      key={snapshot.id}
                      snapshot={snapshot}
                      onCardClick={handleSnapshotCardClick}
                    />
                  ))}
                </div>

                {snapshotsTotalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="btn btn-small"
                      onClick={() => setSnapshotsPage(Math.max(1, snapshotsPage - 1))}
                      disabled={snapshotsPage === 1}
                    >
                      ← Previous
                    </button>
                    <span className="pagination-info">
                      Page {snapshotsPage} of {snapshotsTotalPages} ({snapshotsTotal} snapshots)
                    </span>
                    <button
                      className="btn btn-small"
                      onClick={() => setSnapshotsPage(Math.min(snapshotsTotalPages, snapshotsPage + 1))}
                      disabled={snapshotsPage === snapshotsTotalPages}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Snapshot Details Modal - Removed: Use dedicated SnapshotReportPage instead */}
    </div>
  );
};

export default AdminReportsPage;